import { z } from 'zod'
import type { TFunction } from 'i18next'
import {
  OTC_PURPOSE,
  OTC_SOURCE_OF_FUNDS,
  isOtcPurpose,
  isOtcSourceOfFunds,
  type OtcPurpose,
  type OtcSourceOfFunds,
} from '@paytrie/common/otc'

import { isValidWalletAddressForNetwork } from '@/pages/Buy/utils'
import {
  getNetwork,
  NetworkOptions,
  Networks,
  type NetworkValue,
} from '@/utils/currencies'

const SOURCE_OF_FUNDS_LABEL_KEYS: Record<OtcSourceOfFunds, string> = {
  employment_income: 'otc.sourceOfFunds.employmentIncome',
  business_revenue: 'otc.sourceOfFunds.businessRevenue',
  savings: 'otc.sourceOfFunds.savings',
  investments: 'otc.sourceOfFunds.investments',
  sale_of_assets: 'otc.sourceOfFunds.saleOfAssets',
  inheritance: 'otc.sourceOfFunds.inheritance',
}

export const SOURCE_OF_FUNDS_OPTIONS = OTC_SOURCE_OF_FUNDS.map((value) => ({
  value,
  labelKey: SOURCE_OF_FUNDS_LABEL_KEYS[value],
}))

const PURPOSE_LABEL_KEYS: Record<OtcPurpose, string> = {
  investment: 'otc.purpose.investment',
  business_operations: 'otc.purpose.businessOperations',
  personal_savings: 'otc.purpose.personalSavings',
  goods_and_services: 'otc.purpose.goodsAndServices',
  family_support: 'otc.purpose.familySupport',
}

export const PURPOSE_OPTIONS = OTC_PURPOSE.map((value) => ({
  value,
  labelKey: PURPOSE_LABEL_KEYS[value],
}))

type BankMode = 'saved' | 'new'

export type OtcFormValues = {
  leftSideValue: number
  sourceOfFunds: string
  purpose: string
  wallet: string
  bankMode: BankMode
  bankDetailsId: number | null
  accountHolderName: string
  institutionNumber: string
  transitNumber: string
  accountNumber: string
  nickname: string
}

export const OTC_FORM_DEFAULTS: OtcFormValues = {
  leftSideValue: 0,
  sourceOfFunds: '',
  purpose: '',
  wallet: '',
  bankMode: 'new',
  bankDetailsId: null,
  accountHolderName: '',
  institutionNumber: '',
  transitNumber: '',
  accountNumber: '',
  nickname: '',
}

export type OtcContext = {
  leftSideLabel: string
  rightSideLabel: string
  leftSideValue: number
  isSell: boolean
  network: NetworkValue
}

export const resolveOtcContext = (
  params: URLSearchParams,
  fallback: OtcContext
): OtcContext => {
  const leftSideLabel = params.get('leftSideLabel')
  const rightSideLabel = params.get('rightSideLabel')
  const leftSideValue = Number(params.get('leftSideValue'))

  const hasUrlContext =
    !!leftSideLabel &&
    !!rightSideLabel &&
    Number.isFinite(leftSideValue) &&
    leftSideValue > 0

  if (!hasUrlContext) {
    return fallback
  }

  const isSell = leftSideLabel.toUpperCase() !== 'CAD'
  const coinLabel = isSell ? leftSideLabel : rightSideLabel

  return {
    leftSideLabel,
    rightSideLabel,
    leftSideValue,
    isSell,
    network: getNetwork(coinLabel.split('-')[1] ?? ''),
  }
}

const getWalletInvalidMessage = ({
  isSell,
  network,
  t,
}: {
  isSell: boolean
  network: NetworkValue
  t: TFunction
}) => {
  if (network === Networks.sol) {
    return t(
      isSell
        ? 'sellForm.errors.walletInvalidSolana'
        : 'buyForm.errors.walletInvalidSolana'
    )
  }
  return t(
    isSell
      ? 'sellForm.errors.walletInvalidNetwork'
      : 'buyForm.errors.walletInvalidNetwork',
    { network: NetworkOptions[network].name }
  )
}

export const buildOtcSchema = ({
  isSell,
  network,
  t,
}: {
  isSell: boolean
  network: NetworkValue
  t: TFunction
}) =>
  z
    .object({
      leftSideValue: z.number().positive(t('otc.validation.amount')),
      sourceOfFunds: z
        .string()
        .trim()
        .refine(isOtcSourceOfFunds, t('otc.validation.sourceOfFunds')),
      purpose: z
        .string()
        .trim()
        .refine(isOtcPurpose, t('otc.validation.purpose')),
      wallet: z
        .string()
        .trim()
        .min(1, t('otc.validation.walletRequired'))
        .refine(
          (wallet) => isValidWalletAddressForNetwork(wallet, network),
          getWalletInvalidMessage({ isSell, network, t })
        ),
      bankMode: z.enum(['saved', 'new']),
      bankDetailsId: z.number().nullable(),
      accountHolderName: z.string().trim(),
      institutionNumber: z.string().trim(),
      transitNumber: z.string().trim(),
      accountNumber: z.string().trim(),
      nickname: z.string().trim(),
    })
    .superRefine((values, ctx) => {
      if (!isSell) return

      if (values.bankMode === 'saved') {
        if (values.bankDetailsId === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bankDetailsId'],
            message: t('otc.validation.selectBankAccount'),
          })
        }
        return
      }

      if (values.accountHolderName.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['accountHolderName'],
          message: t('otc.validation.accountHolderRequired'),
        })
      }
      if (!/^\d{3}$/.test(values.institutionNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['institutionNumber'],
          message: t('otc.validation.institution'),
        })
      }
      if (!/^\d{5}$/.test(values.transitNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['transitNumber'],
          message: t('otc.validation.transit'),
        })
      }
      if (!/^\d{7,12}$/.test(values.accountNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['accountNumber'],
          message: t('otc.validation.account'),
        })
      }
    })

export type OtcFormOutput = z.output<ReturnType<typeof buildOtcSchema>>

export const formatBankAccountLabel = (account: {
  nickname: string | null
  bankName: string | null
  accountLast4: string
}): string => {
  const prefix = account.nickname ? `${account.nickname} · ` : ''
  const bank = account.bankName ? `${account.bankName} ` : ''
  return `${prefix}${bank}····${account.accountLast4}`
}
